import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';

import { HttpService } from '../service/http.service';
import { StudentComment } from '../student-comment';
import { StudentContact } from '../student-contact';
import { StudentContactType } from '../student-contact-type';
import { ToastMessageService } from '../toast-message.service';
import { BookingService } from './booking.service';
import { ApiError } from '../api-error';
import { ConfirmationService } from 'primeng/api';

@Injectable({
  providedIn: 'root'
})
export class BookingStudentService {

  studentForm!: FormGroup;
  comments: StudentComment[] = [];
  commentForm!: FormGroup;
  studentContactTypes: StudentContactType[] = [];

  constructor(
    private builder: FormBuilder,
    public bookingService: BookingService,
    private toastService: ToastMessageService,
    private httpService: HttpService,
    private confirmationService: ConfirmationService
  ) { }

  initStudentForm() {
    let student = this.bookingService.booking.student;
    this.studentForm = this.builder.group({
      firstName: student.firstName,
      lastName: student.lastName,
      language: student.language,
      gender: student.gender,
      birthDay: student.birthDay == null ? null : moment(student.birthDay).toDate(),
      nationality: student.nationality,
      mobile: student.mobileNumber,
      phoneNumber: student.phoneNumber,
      email: student.email,
      address: student.address,
      postCode: student.postalCode,
      city: student.city,
      country: student.country,
      allergies: student.allergies,
      hobbies: student.hobbies,
      specialDiet: student.specialDiet,
      smokingOk: student.smokingOk,
      kidsOk: student.kidsOk,
      petsOk: student.petOk,
      otherPreference: student.otherPreference,
      motherLanguage: student.motherLanguage,
      contacts: this.builder.array([]),
      primaryContactType: student.primaryContactType,
      secondaryAddress: student.secondaryAddress,
      secondaryPostCode: student.secondaryPostalCode,
      secondaryCity: student.secondaryCity,
      secondaryCountry: student.secondaryCountry
    });
  }

  initCommentForm() {
    this.commentForm = this.builder.group({
      body: ""
    });
  }

  initStudentContactForm() {
    let array = this.studentForm.get("contacts") as FormArray;
    array.clear();
    for (let contact of this.bookingService.booking.student.contacts) {
      array.controls.push(this.builder.group({
        id: contact.id,
        email: contact.email,
        phoneNumber: contact.phoneNumber,
        mobileNumber: contact.mobileNumber,
        type: contact.type,
        name: contact.name
      }));
    }
  }

  deleteStudentContact(contact: AbstractControl, index: number) {
    let array = this.studentForm.get("contacts") as FormArray;
    if (contact.value.id) {
      this.confirmationService.confirm({
        message: "Are you sure that you want to delete this contact? type: " + contact.value.type.name,
        accept: () => {
          this.httpService.deleteStudentContactById(contact.value.id).subscribe(() => {
            array.removeAt(index);
          });
        }
      });
    } else {
      array.removeAt(index);
    }
  }

  insertStudentContactForm() {
    let array = this.studentForm.get("contacts") as FormArray;
    array.controls.push(this.builder.group({
      id: null,
      email: null,
      phoneNumber: null,
      mobileNumber: null,
      type: [null, Validators.required]
    }));
  }

  updateStudent() {
    let student = this.bookingService.booking.student;
    student.firstName = this.studentForm.value.firstName;
    student.lastName = this.studentForm.value.lastName;
    student.language = this.studentForm.value.language;
    student.gender = this.studentForm.value.gender;
    student.birthDay = this.studentForm.value.birthDay == null ? null : moment(this.studentForm.value.birthDay).utc(true);
    student.nationality = this.studentForm.value.nationality;
    student.mobileNumber = this.studentForm.value.mobile;
    student.phoneNumber = this.studentForm.value.phoneNumber;
    student.email = this.studentForm.value.email;
    student.address = this.studentForm.value.address;
    student.postalCode = this.studentForm.value.postCode;
    student.city = this.studentForm.value.city;
    student.country = this.studentForm.value.country;
    student.allergies = this.studentForm.value.allergies;
    student.hobbies = this.studentForm.value.hobbies;
    student.specialDiet = this.studentForm.value.specialDiet;
    student.smokingOk = this.studentForm.value.smokingOk;
    student.kidsOk = this.studentForm.value.kidsOk;
    student.petOk = this.studentForm.value.petsOk;
    student.otherPreference = this.studentForm.value.otherPreference;
    student.motherLanguage = this.studentForm.value.motherLanguage;
    student.primaryContactType = this.studentForm.value.primaryContactType;
    student.secondaryAddress = this.studentForm.value.secondaryAddress;
    student.secondaryPostalCode = this.studentForm.value.secondaryPostCode;
    student.secondaryCity = this.studentForm.value.secondaryCity;
    student.secondaryCountry = this.studentForm.value.secondaryCountry;
    this.updateStudentContacts();
    this.httpService.putStudent(student).subscribe(res => {
      this.bookingService.booking.student.versionId = res;
      this.toastService.addSuccess("Success", "Student updated");
      this.httpService.getStudentContactsByStudentId(student.id!).subscribe(res => {
        student.contacts = res;
        this.bookingService.sendingUpdateRequest = false;
        this.initStudentContactForm();
      });
    }, error => {
      let apiError = new ApiError(error);
      this.bookingService.sendingUpdateRequest = false;
      this.toastService.addError("Error", apiError.message);
    });
  }

  updateStudentContacts() {
    let contacts = [];
    let contactArray = this.studentForm.get("contacts") as FormArray;
    for (let control of contactArray.controls) {
      if (control.valid) {
        contacts.push(
          new StudentContact(
            control.value.email,
            control.value.phoneNumber,
            control.value.mobileNumber,
            control.value.type,
            control.value.name,
            control.value.id
          ));
      } else {
        this.toastService.addError("Error", "Student contact form shoud be complete");
        throw new Error("Student contacts form invalid");
      }
    }
    this.bookingService.booking.student.contacts = contacts;
  }

  postStudentComment() {
    let comment = new StudentComment(
      this.commentForm.value.body
    );
    this.httpService.postStudentComment(comment, this.bookingService.booking.student.id!).subscribe(() => {
      this.toastService.addSuccess("Success", "Comment is successfully posted");
      this.initStudentComments(this.bookingService.booking.student.id!);
      this.commentForm.get("body")!.reset();
    });
  }

  initStudentComments(studentId: number) {
    this.httpService.getStudentComments(studentId).subscribe(res => {
      this.bookingService.booking.student.studentComments = res;
    });
  }
}
